import React, { useMemo, useState } from "react";
import { useGeodataQuery } from "../hooks/useGeodataQuery";
import { REGULATORY_ROWS, WATER_DC_REGULATIONS, filterRegulatoryRows, filterWaterDcRows } from "../data/regulatoryCatalog";

function fmtTemp(v) {
  if (v == null || Number.isNaN(v)) return "—";
  return `${Number(v).toFixed(1)}°C`;
}

export default function RegulationsPage({ lat, lng, siteName }) {
  const [q, setQ] = useState("");
  const { data: envelope, isFetching, isError, error, refetch, dataUpdatedAt } = useGeodataQuery(lat, lng, 15);

  const rows = useMemo(() => filterRegulatoryRows(REGULATORY_ROWS, q), [q]);
  const waterRows = useMemo(() => filterWaterDcRows(WATER_DC_REGULATIONS, q), [q]);

  const iso = envelope?.lastOkAt
    ? new Date(envelope.lastOkAt).toISOString()
    : dataUpdatedAt
      ? new Date(dataUpdatedAt).toISOString()
      : null;

  const payload = envelope?.data;
  const marine = payload?.open_meteo_marine;
  const marineSrc = marine?.source;

  return (
    <div className="animate-ds-fade-in space-y-8">
      <section aria-labelledby="dc-water-heading" className="card overflow-hidden p-0">
        <div className="border-b border-border bg-canvas-subtle px-4 py-3 md:px-6">
          <h2 id="dc-water-heading" className="text-sm font-bold text-fg">
            Datacenter water &amp; thermal screening
          </h2>
          <p className="mt-1 max-w-3xl text-xs text-fg-secondary">
            EPA Region 10 guidance, NPDES thermal variances (§316(a)), and power-plant plume analogues are starting points for underwater heat rejection. Pair rules below with{" "}
            <strong className="font-medium text-fg">live SST</strong> for {siteName || "selected coordinates"}.
          </p>
        </div>
        <div className="grid gap-4 p-4 md:grid-cols-2 md:p-6">
          <div className="rounded-ds-xl border border-border bg-canvas-subtle p-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-accent">Live SST (Open-Meteo marine)</span>
              {iso && (
                <span className="rounded-full border border-border bg-surface px-2 py-0.5 text-[10px] font-medium text-fg-secondary">
                  Last updated: {iso}
                </span>
              )}
            </div>
            {marine?.error ? (
              <p className="text-xs text-danger">{marine.error}</p>
            ) : (
              <p className="text-sm text-fg-secondary">
                Sea surface: <strong className="text-fg">{fmtTemp(marine?.current?.sea_surface_temperature)}</strong>
                {marine?._mock ? <span className="ml-2 text-[10px] font-semibold uppercase text-warning"> Cached / fallback</span> : null}
              </p>
            )}
            {marineSrc && (
              <a href={marineSrc} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-xs font-medium text-accent hover:underline">
                Source: Open-Meteo marine API
              </a>
            )}
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-3 rounded-ds border border-border bg-surface px-3 py-1.5 text-xs font-medium text-fg-secondary hover:bg-canvas-subtle focus:outline-none focus-visible:shadow-focus"
            >
              {isFetching ? "Refreshing…" : "Refresh live SST"}
            </button>
            {envelope?.fromCache && envelope?.err && (
              <p className="mt-2 text-xs text-warning" role="status">
                {envelope.err}
              </p>
            )}
            {isError && !payload && (
              <p className="mt-2 text-xs text-danger" role="alert">
                {(error && error.message) || "Could not load geodata."}
              </p>
            )}
          </div>
          <ul className="space-y-3 text-sm">
            {waterRows.map((r) => (
              <li key={r.id} className="rounded-ds border border-border bg-surface p-3">
                <div className="font-semibold text-fg">{r.title}</div>
                <p className="mt-1 text-xs text-fg-secondary">{r.summary}</p>
                <a href={r.sourceUrl} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline">
                  <span className="rounded-full bg-success/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-success">Verify</span>
                  Open primary source
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section aria-labelledby="regs-table-heading">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 id="regs-table-heading" className="text-lg font-bold text-fg">
              Regulatory matrix
            </h2>
            <p className="mt-1 max-w-2xl text-sm text-fg-secondary">
              Explore thresholds from EPA, states, provinces, and CCME with direct links for compliance fact-checking.
            </p>
          </div>
          <label className="block w-full sm:max-w-xs">
            <span className="sr-only">Filter regulations</span>
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search (e.g. thermal discharge, 316a)…"
              className="w-full rounded-ds border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus-visible:shadow-focus"
            />
          </label>
        </div>

        <div className="overflow-x-auto rounded-ds-xl border border-border bg-surface shadow-card">
          <table className="w-full min-w-[720px] border-collapse text-left text-xs">
            <thead>
              <tr className="border-b border-border bg-canvas-subtle text-[10px] font-semibold uppercase tracking-wide text-fg-tertiary">
                <th className="px-3 py-2">Jurisdiction</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Instrument</th>
                <th className="px-3 py-2">Thresholds</th>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2">Gap</th>
                <th className="px-3 py-2">Citations</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b border-border last:border-0 hover:bg-canvas-subtle/80">
                  <td className="px-3 py-3 align-top text-fg-secondary">{row.jurisdictionLabel}</td>
                  <td className="px-3 py-3 align-top">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                        row.type === "BINDING" ? "bg-success/15 text-success" : "bg-warning/15 text-warning"
                      }`}
                    >
                      {row.type}
                    </span>
                  </td>
                  <td className="px-3 py-3 align-top font-medium text-fg">{row.instrument}</td>
                  <td className="max-w-[200px] px-3 py-3 align-top text-fg-secondary">{row.thresholds}</td>
                  <td className="px-3 py-3 align-top">
                    <a href={row.sourceUrl} target="_blank" rel="noopener noreferrer" className="font-medium text-accent hover:underline">
                      Primary ↗
                    </a>
                  </td>
                  <td className="max-w-[180px] px-3 py-3 align-top text-warning">{row.gap}</td>
                  <td className="max-w-[220px] px-3 py-3 align-top text-fg-tertiary">{row.citations}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && <p className="mt-3 text-sm text-fg-tertiary">No rows match “{q}”.</p>}
      </section>

    </div>
  );
}
